import { useState, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  ChevronLeft, ChevronRight, Search, X, MoreHorizontal,
  CheckCircle, XCircle, Info, RefreshCw, Eye, Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";

const API = `${process.env.REACT_APP_API_URL}/api`;
const fmt = (v) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(v || 0);

const STATUS_BADGE = {
  paid:     "bg-verdict-real/10 text-verdict-real",
  pending:  "bg-amber-500/10 text-amber-400",
  rejected: "bg-verdict-fake/10 text-verdict-fake",
  refunded: "bg-secondary text-muted-foreground",
};

function Pagination({ page, pages, onPage }) {
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-border">
      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-sm" onClick={() => onPage(page - 1)} disabled={page <= 1}><ChevronLeft className="w-3.5 h-3.5" /></Button>
      <span className="text-xs font-mono text-muted-foreground">{page} / {pages}</span>
      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-sm" onClick={() => onPage(page + 1)} disabled={page >= pages}><ChevronRight className="w-3.5 h-3.5" /></Button>
    </div>
  );
}

/* ── Transaction Detail Modal ────────────────────────────────────────────── */
function TxDetailModal({ txn, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const txId = txn.id || txn.order_id;
        const res = await axios.get(`${API}/admin/transactions/${txId}/detail`, { withCredentials: true });
        setDetail(res.data);
      } catch {
        setDetail(txn);
      } finally { setLoading(false); }
    };
    load();
  }, [txn]);

  const fmtDate = (iso) => {
    if (!iso) return "\u2014";
    try { return new Date(iso).toLocaleString("id-ID", { timeZone: "Asia/Jakarta", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  const d = detail || txn;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 12 }}
        className="relative bg-background border border-border rounded-md shadow-2xl w-full max-w-md" data-testid="tx-detail-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="font-heading text-lg font-bold">Detail Transaksi</h2>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-sm" onClick={onClose} data-testid="close-tx-modal"><X className="w-4 h-4" /></Button>
        </div>
        <div className="p-6 space-y-4">
          {loading ? (
            <div className="py-8 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <span className={`text-xs font-mono px-2 py-0.5 rounded-sm ${STATUS_BADGE[d.payment_status] || "bg-secondary text-muted-foreground"}`}>{d.payment_status || "\u2014"}</span>
                <span className="font-heading text-xl font-bold text-verdict-real">{fmt(d.amount)}</span>
              </div>
              <div className="space-y-2">
                {[
                  ["Order ID", d.order_id || d.id || "\u2014"],
                  ["Type", (d.type || "subscription")],
                  ["Plan", d.plan_id || "\u2014"],
                  ...(d.credits ? [["Credits", d.credits]] : []),
                  ["User", d.user_name ? `${d.user_name} (${d.user_email})` : (d.user_id || "\u2014")],
                  ...(d.user_plan ? [["User Plan", d.user_plan]] : []),
                  ["Payment Method", d.payment_type || d.payment_method || "\u2014"],
                  ["Created", fmtDate(d.created_at)],
                  ...(d.paid_at ? [["Paid At", fmtDate(d.paid_at)]] : []),
                  ...(d.admin_updated_at ? [["Admin Updated", fmtDate(d.admin_updated_at)]] : []),
                  ...(d.midtrans_transaction_id ? [["Midtrans ID", d.midtrans_transaction_id]] : []),
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{k}</span>
                    <span className="font-mono text-xs text-right max-w-[220px] truncate" title={String(v)}>{String(v)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}

/* ── Action Menu ─────────────────────────────────────────────────────────── */
function TxActionMenu({ txn, onRefresh, onViewDetail }) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  const updateMenuPosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const menuWidth = 208;
    const left = Math.min(
      window.innerWidth - menuWidth - 8,
      Math.max(8, rect.right - menuWidth),
    );
    const top = Math.min(window.innerHeight - 8, rect.bottom + 6);
    setMenuPos({ top, left });
  }, []);

  useEffect(() => {
    const h = (e) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(e.target) &&
        menuRef.current &&
        !menuRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    if (!open) return;
    updateMenuPosition();
    const handleUpdate = () => updateMenuPosition();
    window.addEventListener("resize", handleUpdate);
    window.addEventListener("scroll", handleUpdate, true);
    return () => {
      window.removeEventListener("resize", handleUpdate);
      window.removeEventListener("scroll", handleUpdate, true);
    };
  }, [open, updateMenuPosition]);

  const updateStatus = async (status) => {
    setOpen(false);
    try {
      const txId = txn.id || txn.order_id;
      await axios.patch(`${API}/admin/transactions/${txId}/status`, { status }, { withCredentials: true });
      toast.success(`Transaction marked as ${status}`);
      onRefresh();
    } catch { toast.error("Failed to update status"); }
  };

  const StatusBtn = ({ status, Icon, label, cls }) => (
    txn.payment_status !== status ? (
      <button onClick={() => updateStatus(status)}
        className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs hover:bg-secondary/60 transition-colors text-left ${cls}`}>
        <Icon className="w-3.5 h-3.5" /> {label}
      </button>
    ) : null
  );

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => {
          if (!open) updateMenuPosition();
          setOpen((v) => !v);
        }}
        className="w-7 h-7 rounded-sm flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
        data-testid={`tx-action-${(txn.id || txn.order_id)?.slice(0,8)}`}>
        <MoreHorizontal className="w-4 h-4" />
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          style={{ position: "fixed", top: `${menuPos.top}px`, left: `${menuPos.left}px` }}
          className="z-[120] w-52 rounded-sm border border-border bg-popover shadow-lg overflow-hidden"
        >
          <button onClick={() => { setOpen(false); onViewDetail(txn); }}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-xs hover:bg-secondary/60 transition-colors text-foreground text-left"
            data-testid={`tx-view-detail-${(txn.id || txn.order_id)?.slice(0,8)}`}>
            <Eye className="w-3.5 h-3.5" /> View Detail
          </button>
          <div className="border-t border-border" />
          <StatusBtn status="paid"     Icon={CheckCircle}  label="Mark as Paid"     cls="text-verdict-real" />
          <StatusBtn status="pending"  Icon={RefreshCw}    label="Mark as Pending"  cls="text-amber-400" />
          <StatusBtn status="rejected" Icon={XCircle}      label="Reject"           cls="text-verdict-fake" />
          <StatusBtn status="refunded" Icon={Info}         label="Mark as Refunded" cls="text-muted-foreground" />
        </div>,
        document.body,
      )}
    </div>
  );
}

/* ── Main ────────────────────────────────────────────────────────────────── */
export default function AdminTransactions() {
  const { t } = useTranslation();
  const [data, setData] = useState({ transactions: [], total: 0, page: 1, pages: 1, total_revenue: 0 });
  const [search, setSearch] = useState("");
  const [txType, setTxType] = useState("all");
  const [txStatus, setTxStatus] = useState("all");
  const [txPlan, setTxPlan] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [detailTxn, setDetailTxn] = useState(null);

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page };
      if (search.trim()) params.search = search.trim();
      if (txType !== "all") params.txn_type = txType;
      if (txStatus !== "all") params.status = txStatus;
      if (txPlan !== "all") params.plan = txPlan;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await axios.get(`${API}/admin/transactions`, { params, withCredentials: true });
      setData(res.data);
    } catch { } finally { setLoading(false); }
  }, [search, txType, txStatus, txPlan, dateFrom, dateTo]);

  // Intentionally refresh on filter/date changes; search runs on submit.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(1); }, [txType, txStatus, txPlan, dateFrom, dateTo]);

  const handleSearch = (e) => { e.preventDefault(); load(1); };
  const clearFilters = () => { setSearch(""); setTxType("all"); setTxStatus("all"); setTxPlan("all"); setDateFrom(""); setDateTo(""); };
  const hasFilters = search || txType !== "all" || txStatus !== "all" || txPlan !== "all" || dateFrom || dateTo;

  const TH = ["admin.txPage.thUser", "admin.txPage.thType", "admin.txPage.thPlanCredits", "admin.txPage.thAmount", "admin.txPage.thStatus", "admin.txPage.thDate", ""];

  return (
    <div className="p-6 md:p-8" data-testid="admin-transactions-page">
      <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold">{t("admin.txPage.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("admin.txPage.totalDesc", { total: data.total })}</p>
        </div>
        <div className="rounded-md border border-verdict-real/20 bg-verdict-real/5 px-5 py-3">
          <div className="text-xs font-mono text-muted-foreground uppercase tracking-widest mb-0.5">{t("admin.txPage.totalRevenue")}</div>
          <div className="font-heading text-xl font-bold text-verdict-real" data-testid="total-revenue">{fmt(data.total_revenue)}</div>
        </div>
      </div>

      {/* Filters — matching AuditLog card style */}
      <div className="mb-6 rounded-md border border-border bg-card p-4" data-testid="tx-filters">
        <div className="flex flex-wrap items-end gap-3">
          <form onSubmit={handleSearch} className="flex-1 min-w-[200px]">
            <label className="text-xs font-mono text-muted-foreground mb-1 block">{t("admin.txPage.searchPlaceholder")}</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder={t("admin.txPage.searchPlaceholder")}
                className="w-full bg-background border border-border rounded-sm pl-8 pr-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-brand-primary"
                data-testid="tx-search" />
            </div>
          </form>
          <div className="min-w-[120px]">
            <label className="text-xs font-mono text-muted-foreground mb-1 block">Type</label>
            <select value={txType} onChange={e => setTxType(e.target.value)}
              className="w-full bg-background border border-border rounded-sm px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-brand-primary"
              data-testid="filter-tx-type">
              <option value="all">{t("admin.txPage.allTypes")}</option>
              <option value="subscription">Subscription</option>
              <option value="credits">Credits</option>
            </select>
          </div>
          <div className="min-w-[120px]">
            <label className="text-xs font-mono text-muted-foreground mb-1 block">Status</label>
            <select value={txStatus} onChange={e => setTxStatus(e.target.value)}
              className="w-full bg-background border border-border rounded-sm px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-brand-primary"
              data-testid="filter-tx-status">
              <option value="all">{t("admin.txPage.allStatus")}</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div className="min-w-[120px]">
            <label className="text-xs font-mono text-muted-foreground mb-1 block">Plan</label>
            <select value={txPlan} onChange={e => setTxPlan(e.target.value)}
              className="w-full bg-background border border-border rounded-sm px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-brand-primary"
              data-testid="filter-tx-plan">
              <option value="all">{t("admin.txPage.allPlans")}</option>
              {["starter", "pro", "business"].map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-mono text-muted-foreground mb-1 block">{t("admin.audit.dateFrom")}</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="bg-background border border-border rounded-sm px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-brand-primary"
              data-testid="tx-date-from" />
          </div>
          <div>
            <label className="text-xs font-mono text-muted-foreground mb-1 block">{t("admin.audit.dateTo")}</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="bg-background border border-border rounded-sm px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-brand-primary"
              data-testid="tx-date-to" />
          </div>
          <div className="flex items-center gap-1.5">
            {hasFilters && (
              <Button size="sm" variant="ghost" className="rounded-sm gap-1 text-xs h-8 text-muted-foreground" onClick={clearFilters} data-testid="clear-tx-filters-btn">
                <X className="w-3 h-3" /> Reset
              </Button>
            )}
            <Button size="sm" variant="outline" className="rounded-sm gap-1.5 text-xs h-8" onClick={() => load(data.page)} disabled={loading} data-testid="tx-refresh-btn">
              <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-mono text-muted-foreground">
          {t("admin.audit.showing", { count: data.transactions.length, total: data.total })}
        </span>
      </div>

      <div className="rounded-md border border-border bg-card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-secondary/40">
                {TH.map((h, i) => (
                  <th key={i} className="px-4 py-3 text-left text-xs font-mono uppercase tracking-widest text-muted-foreground">{h ? t(h) : ""}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="py-8 text-center text-sm text-muted-foreground">{t("admin.txPage.loading")}</td></tr>
              ) : data.transactions.map((txn, i) => (
                <tr key={i} className="border-b border-border last:border-0 hover:bg-secondary/20 transition-colors">
                  <td className="px-4 py-3 max-w-[180px]">
                    <div className="text-sm font-medium truncate">{txn.user_name || "\u2014"}</div>
                    <div className="text-xs font-mono text-muted-foreground truncate">{txn.user_email || "\u2014"}</div>
                  </td>
                  <td className="px-4 py-3 text-xs font-mono capitalize">{txn.type || "subscription"}</td>
                  <td className="px-4 py-3 text-xs font-mono text-muted-foreground capitalize">
                    {txn.plan_id || (txn.credits ? `${txn.credits} ${t("admin.txPage.credits")}` : "\u2014")}
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-verdict-real">{fmt(txn.amount)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-mono px-2 py-0.5 rounded-sm ${STATUS_BADGE[txn.payment_status] || "bg-secondary text-muted-foreground"}`}>
                      {txn.payment_status || "\u2014"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-muted-foreground">
                    {txn.created_at ? new Date(txn.created_at).toLocaleDateString("id-ID", { timeZone: "Asia/Jakarta" }) : "\u2014"}
                  </td>
                  <td className="px-4 py-3">
                    <TxActionMenu txn={txn} onRefresh={() => load(data.page)} onViewDetail={setDetailTxn} />
                  </td>
                </tr>
              ))}
              {!loading && data.transactions.length === 0 && (
                <tr><td colSpan={7} className="py-12 text-center text-sm text-muted-foreground">
                  {hasFilters ? t("admin.txPage.noDataFilter") : t("admin.txPage.noData")}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination page={data.page} pages={data.pages} onPage={load} />
      </div>

      <AnimatePresence>
        {detailTxn && <TxDetailModal txn={detailTxn} onClose={() => setDetailTxn(null)} />}
      </AnimatePresence>
    </div>
  );
}
